import { useCallback, useEffect, useMemo, useState } from 'react'
import { boxApi } from '../utils/api'

function spreadTargets(desiredCount, totalSlots) {
  if (desiredCount <= 0) return []
  if (totalSlots <= 0) return [0]
  return Array.from({ length: desiredCount }, (_, idx) =>
    Math.min(totalSlots - 1, Math.floor((totalSlots * (idx + 0.5)) / desiredCount))
  )
}

function normalizeSlotMembers(slotValue) {
  // Handle new format: array of {member_id, custom_amount} objects
  // Also handles old format: strings or array of strings
  if (!slotValue) return []
  
  if (Array.isArray(slotValue)) {
    return slotValue
      .filter(Boolean)
      .map((item) => {
        if (typeof item === 'object' && item.member_id) {
          // Already new format
          return {
            member_id: String(item.member_id),
            custom_amount: item.custom_amount != null ? parseFloat(item.custom_amount) : null
          }
        }
        // Old format: string
        return { member_id: String(item), custom_amount: null }
      })
  }
  
  if (typeof slotValue === 'string' && slotValue) {
    // Old format: single string
    return [{ member_id: String(slotValue), custom_amount: null }]
  }
  
  return []
}

function normalizeAssignments(assignments, totalSlots) {
  if (!totalSlots || totalSlots <= 0) return []
  
  // Check if it's the flattened format (array of {slot_index, member_id, custom_amount})
  if (Array.isArray(assignments) && assignments.length > 0 && 
      typeof assignments[0] === 'object' && assignments[0].slot_index != null) {
    // Flattened format from Firestore: reconstruct nested structure
    const slots = Array.from({ length: totalSlots }, () => [])
    assignments.forEach((item) => {
      const slotIdx = item.slot_index || 0
      if (slotIdx >= 0 && slotIdx < totalSlots) {
        slots[slotIdx].push({
          member_id: String(item.member_id || ''),
          custom_amount: item.custom_amount != null ? parseFloat(item.custom_amount) : null
        })
      }
    })
    return slots.map((slot) => slot.length ? slot : [{ member_id: '', custom_amount: null }])
  }
  
  // Nested format (old or new)
  return Array.from({ length: totalSlots }, (_, idx) => {
    const slotValue = Array.isArray(assignments) ? assignments[idx] : null
    const normalized = normalizeSlotMembers(slotValue)
    return normalized.length ? normalized : [{ member_id: '', custom_amount: null }]
  })
}

function buildAssignments(members, totalSlots) {
  if (!totalSlots || totalSlots <= 0) return []
  const slots = Array.from({ length: totalSlots }, () => [])
  if (!members.length) {
    return Array.from({ length: totalSlots }, () => [''])
  }

  const base = Math.max(1, Math.floor(totalSlots / members.length))
  const remainder = totalSlots % members.length

  members.forEach((member, idx) => {
    const desired = member.desired_payouts_per_cycle ?? base + (idx < remainder ? 1 : 0)
    const targets = spreadTargets(desired, totalSlots)
    targets.forEach((slotIdx) => {
      if (!slots[slotIdx]) return
      slots[slotIdx].push({ member_id: member.id, custom_amount: null })
    })
  })

  // Make sure every member appears at least once
  const counts = slots.flat().reduce((acc, memberObj) => {
    const memberId = typeof memberObj === 'object' ? memberObj?.member_id : memberObj
    if (!memberId) return acc
    acc[memberId] = (acc[memberId] || 0) + 1
    return acc
  }, {})
  members.forEach((member, idx) => {
    if (!counts[member.id]) {
      const target = totalSlots > 0 ? idx % totalSlots : 0
      slots[target]?.push({ member_id: member.id, custom_amount: null })
      counts[member.id] = 1
    }
  })

  // Fill any empty slots with a fallback member so there is always at least one payout per slot
  slots.forEach((slot, idx) => {
    if (!slot.length) {
      const fallback = members[idx % members.length]?.id
      if (fallback) {
        slot.push({ member_id: fallback, custom_amount: null })
      }
    }
  })

  return slots.map((slot) => (slot.length ? slot : [{ member_id: '', custom_amount: null }]))
}

export default function ScheduleBuilder({ boxId, box, onSaved = () => {} }) {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [cycleMonths, setCycleMonths] = useState(() => box?.cycle_duration_months || 6)
  const [payoutsPerMonth, setPayoutsPerMonth] = useState(() => box?.payouts_per_month || 1)
  const [assignments, setAssignments] = useState([])
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [scheduleError, setScheduleError] = useState(null)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const totalSlots = cycleMonths * payoutsPerMonth

  const getBaseContribution = useCallback(
    (member) => {
      const raw = member?.contribution_amount ?? box?.contribution_amount ?? 0
      const num = Number(raw)
      return Number.isNaN(num) ? 0 : num
    },
    [box?.contribution_amount]
  )

  // Calculate member payout totals for validation
  const memberPayoutTotals = useMemo(() => {
    const totals = {}
    const expected = {}
    
    members.forEach((member) => {
      const contribution = getBaseContribution(member)
      expected[member.id] = contribution * cycleMonths
      totals[member.id] = { custom: 0, auto: 0, slots: [], expected: expected[member.id] }
    })
    
    assignments.forEach((slot, slotIdx) => {
      slot.forEach((memberObj) => {
        const memberId = typeof memberObj === 'object' ? memberObj?.member_id : memberObj
        if (!memberId || !totals[memberId]) return
        
        if (typeof memberObj === 'object' && memberObj.custom_amount != null) {
          totals[memberId].custom += memberObj.custom_amount
          totals[memberId].slots.push({ slotIdx, type: 'custom', amount: memberObj.custom_amount })
        } else {
          totals[memberId].slots.push({ slotIdx, type: 'auto' })
        }
      })
    })
    
    // Calculate auto amounts
    Object.keys(totals).forEach((memberId) => {
      const autoSlots = totals[memberId].slots.filter(s => s.type === 'auto')
      const remaining = totals[memberId].expected - totals[memberId].custom
      totals[memberId].auto = autoSlots.length > 0 ? remaining / autoSlots.length : 0
      totals[memberId].autoSlotsCount = autoSlots.length
      totals[memberId].total = totals[memberId].custom + (totals[memberId].auto * totals[memberId].autoSlotsCount)
      totals[memberId].difference = totals[memberId].total - totals[memberId].expected
    })
    
    return totals
  }, [assignments, members, cycleMonths, getBaseContribution])

  useEffect(() => {
    setCycleMonths(box?.cycle_duration_months || 6)
    setPayoutsPerMonth(box?.payouts_per_month || 1)
  }, [box?.cycle_duration_months, box?.payouts_per_month])

  const loadMembers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await boxApi.getMembers(boxId)
      const activeMembers = data.filter((member) => (member.status || 'active') !== 'removed')
      setMembers(activeMembers)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [boxId])

  useEffect(() => {
    loadMembers()
  }, [loadMembers])

  useEffect(() => {
    if (box?.schedule_assignments && Array.isArray(box.schedule_assignments) && box.schedule_assignments.length > 0) {
      // Check if it's flattened format (has slot_index) or nested format
      const isFlattened = typeof box.schedule_assignments[0] === 'object' && box.schedule_assignments[0].slot_index != null
      
      if (isFlattened) {
        // For flattened format, use box values as source of truth
        const boxCycleMonths = box?.cycle_duration_months || cycleMonths
        const boxPayoutsPerMonth = box?.payouts_per_month || payoutsPerMonth
        const boxTotalSlots = boxCycleMonths * boxPayoutsPerMonth
        
        // Calculate from data as fallback if box values not available
        const maxSlotIndex = Math.max(...box.schedule_assignments.map(item => item.slot_index || 0))
        const calculatedTotalSlots = maxSlotIndex + 1
        
        // Use box values if available, otherwise use calculated
        const actualTotalSlots = box?.cycle_duration_months && box?.payouts_per_month 
          ? boxTotalSlots 
          : Math.max(calculatedTotalSlots, totalSlots)
        
        // Update cycle months and payouts to match box values
        if (box?.cycle_duration_months && box.cycle_duration_months !== cycleMonths) {
          setCycleMonths(box.cycle_duration_months)
        }
        if (box?.payouts_per_month && box.payouts_per_month !== payoutsPerMonth) {
          setPayoutsPerMonth(box.payouts_per_month)
        }
        
        setAssignments(normalizeAssignments(box.schedule_assignments, actualTotalSlots))
        setDirty(false)
      } else {
        // Nested format
        const isNested = Array.isArray(box.schedule_assignments[0]) || 
                         (box.schedule_assignments.length === totalSlots && 
                          (typeof box.schedule_assignments[0] === 'string' || 
                           (typeof box.schedule_assignments[0] === 'object' && box.schedule_assignments[0].member_id)))
        
        if (isNested) {
          setAssignments(normalizeAssignments(box.schedule_assignments, totalSlots))
          setDirty(false)
        } else {
          setAssignments(buildAssignments(members, totalSlots))
          setDirty(false)
        }
      }
    } else {
      setAssignments(buildAssignments(members, totalSlots))
      setDirty(false)
    }
  }, [box?.schedule_assignments, box?.cycle_duration_months, box?.payouts_per_month, members, totalSlots, cycleMonths, payoutsPerMonth])

  useEffect(() => {
    const fetchSchedule = async () => {
      if (!boxId) return
      setScheduleError(null)
      try {
        const schedule = await boxApi.getSchedule(boxId)
        if (Array.isArray(schedule) && schedule.length) {
          const maxMonth = Math.max(...schedule.map((item) => item.month || 1))
          const maxSlot = Math.max(...schedule.map((item) => item.slot || 1))
          if (!box?.schedule_assignments || box.schedule_assignments.length === 0) {
            const normalizedSlots = Array.from({ length: maxMonth * maxSlot }, () => [])
            schedule.forEach((item) => {
              const slotIdx = ((item.month || 1) - 1) * maxSlot + ((item.slot || 1) - 1)
              if (!normalizedSlots[slotIdx]) {
                normalizedSlots[slotIdx] = []
              }
              // Store with member_id and no custom_amount (will be calculated)
              normalizedSlots[slotIdx].push({ member_id: item.member_id, custom_amount: null })
            })
            setCycleMonths(maxMonth)
            setPayoutsPerMonth(maxSlot)
            setAssignments(normalizeAssignments(normalizedSlots, maxMonth * maxSlot))
            setDirty(false)
          }
        }
      } catch (err) {
        setScheduleError(err.message)
      }
    }
    fetchSchedule()
  }, [boxId, box?.schedule_assignments])

  const assignmentCounts = useMemo(() => {
    return assignments.reduce((acc, slotMembers) => {
      slotMembers.forEach((memberObj) => {
        const memberId = typeof memberObj === 'object' ? memberObj?.member_id : memberObj
        if (!memberId) return
        acc[memberId] = (acc[memberId] || 0) + 1
      })
      return acc
    }, {})
  }, [assignments])

  const allSlotsFilled = assignments.length === totalSlots && assignments.every((slot) => 
    slot.some((memberObj) => {
      const memberId = typeof memberObj === 'object' ? memberObj?.member_id : memberObj
      return Boolean(memberId)
    })
  )
  const allMembersAssigned = members.every((member) => (assignmentCounts[member.id] || 0) > 0)

  const scheduleMatrix = useMemo(() => {
    return Array.from({ length: cycleMonths }, (_, monthIndex) => {
      return Array.from({ length: payoutsPerMonth }, (_, slotIndex) => {
        const assignmentIndex = monthIndex * payoutsPerMonth + slotIndex
        return assignments[assignmentIndex] || [{ member_id: '', custom_amount: null }]
      })
    })
  }, [assignments, cycleMonths, payoutsPerMonth])

  const totalPayoutEvents = useMemo(() => {
    return assignments.reduce((acc, slot) => {
      return acc + slot.filter((memberObj) => {
        const memberId = typeof memberObj === 'object' ? memberObj?.member_id : memberObj
        return Boolean(memberId)
      }).length
    }, 0)
  }, [assignments])

  const stackedSlots = useMemo(() => {
    return assignments.filter((slot) => 
      slot.filter((memberObj) => {
        const memberId = typeof memberObj === 'object' ? memberObj?.member_id : memberObj
        return Boolean(memberId)
      }).length > 1
    ).length
  }, [assignments])

  const handleAssignmentChange = (slotIndex, memberIndex, memberId) => {
    setDirty(true)
    setAssignments((prev) => {
      const next = prev.map((slot) => slot.map((item) => ({ ...item })))
      if (!next[slotIndex]) {
        next[slotIndex] = []
      }
      // Preserve custom_amount if exists, otherwise set to null
      const existing = next[slotIndex][memberIndex]
      next[slotIndex][memberIndex] = {
        member_id: memberId || '',
        custom_amount: existing?.custom_amount ?? null
      }
      return next
    })
  }

  const handleAmountChange = (slotIndex, memberIndex, amount) => {
    setDirty(true)
    setAssignments((prev) => {
      const next = prev.map((slot) => slot.map((item) => ({ ...item })))
      if (!next[slotIndex] || !next[slotIndex][memberIndex]) return next
      
      const newAmount = amount === '' || amount === null ? null : parseFloat(amount)
      next[slotIndex][memberIndex] = {
        ...next[slotIndex][memberIndex],
        custom_amount: isNaN(newAmount) ? null : newAmount
      }
      return next
    })
  }

  const handleResetAmount = (slotIndex, memberIndex) => {
    setDirty(true)
    setAssignments((prev) => {
      const next = prev.map((slot) => slot.map((item) => ({ ...item })))
      if (next[slotIndex] && next[slotIndex][memberIndex]) {
        next[slotIndex][memberIndex] = {
          ...next[slotIndex][memberIndex],
          custom_amount: null
        }
      }
      return next
    })
  }

  const handleAddSlotMember = (slotIndex) => {
    setDirty(true)
    setAssignments((prev) => {
      const next = prev.map((slot) => slot.map((item) => ({ ...item })))
      next[slotIndex] = [...(next[slotIndex] || []), { member_id: '', custom_amount: null }]
      return next
    })
  }

  const handleRemoveSlotMember = (slotIndex, memberIndex) => {
    setDirty(true)
    setAssignments((prev) => {
      const next = prev.map((slot) => slot.map((item) => ({ ...item })))
      if (!next[slotIndex]) return next
      next[slotIndex].splice(memberIndex, 1)
      if (next[slotIndex].length === 0) {
        next[slotIndex] = [{ member_id: '', custom_amount: null }]
      }
      return next
    })
  }

  const handleAutoDistribute = () => {
    setAssignments(buildAssignments(members, totalSlots))
    setDirty(false)
  }

  const handleClearAssignments = () => {
    setDirty(true)
    setAssignments(Array.from({ length: totalSlots }, () => [{ member_id: '', custom_amount: null }]))
  }

  const handleCycleMonthsChange = (event) => {
    const parsed = parseInt(event.target.value, 10)
    const normalized = Number.isNaN(parsed) ? 1 : Math.max(1, parsed)
    setCycleMonths(normalized)
    setDirty(true)
  }

  const handlePayoutsPerMonthChange = (event) => {
    const parsed = parseInt(event.target.value, 10)
    const normalized = Number.isNaN(parsed) ? 1 : Math.max(1, parsed)
    setPayoutsPerMonth(normalized)
    setDirty(true)
  }

  const handleSave = async () => {
    setSaveError(null)
    setSaveSuccess(false)
    
    // Filter out empty member assignments, but keep structure
    const normalizedAssignments = assignments.map((slot) => 
      slot.filter((memberObj) => {
        const memberId = typeof memberObj === 'object' ? memberObj?.member_id : memberObj
        return Boolean(memberId)
      })
    )
    
    if (normalizedAssignments.length !== totalSlots || normalizedAssignments.some((slot) => slot.length === 0)) {
      setSaveError('Assign at least one member to every payout slot.')
      return
    }
    if (!allMembersAssigned) {
      setSaveError('Make sure every member has at least one payout slot.')
      return
    }
    
    try {
      setSaving(true)
      await boxApi.updateSchedule(boxId, {
        cycle_duration_months: cycleMonths,
        payouts_per_month: payoutsPerMonth,
        assignments: normalizedAssignments,
      })
      setDirty(false)
      setSaveSuccess(true)
      try {
        await boxApi.getSchedule(boxId)
        setScheduleError(null)
      } catch (err) {
        setScheduleError(err.message)
      }
      onSaved()
    } catch (err) {
      setSaveError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const openConfirm = () => {
    setSaveError(null)
    setSaveSuccess(false)
    setConfirmOpen(true)
  }

  if (loading) {
    return (
      <div className="p-6 bg-white border-b border-gray-100">
        <p className="text-gray-500 text-sm">Loading members for the schedule builder...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border-b border-red-200 rounded-2xl">
        <div className="flex items-center justify-between">
          <p className="text-red-800 text-sm">Unable to load members: {error}</p>
          <button
            onClick={loadMembers}
            className="px-3 py-1.5 rounded-full text-xs font-semibold bg-red-600 text-white hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (members.length === 0) {
    return (
      <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-2xl mb-6">
        <p className="text-yellow-900 text-sm">
          Invite at least one member to this box to start building a payout schedule.
        </p>
      </div>
    )
  }

  return (
    <div className="border-b border-gray-100">
      <div className="p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Schedule Builder (beta)</h3>
            <p className="text-sm text-gray-500">
              Configure cycle length, payouts per month, and assign which members receive each payout slot. Multiple
              members can share the same slot—use this to give members with higher desired payout counts more than one
              payout in the cycle. Saves run a guardrail so monthly payouts never exceed total monthly contributions.
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={handleAutoDistribute}
              className="inline-flex items-center rounded-full bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-700 hover:bg-primary-100"
            >
              Auto distribute
            </button>
            <button
              type="button"
              onClick={handleClearAssignments}
              className="inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-100"
            >
              Clear all
            </button>
            <button
              type="button"
              disabled={saving || !dirty}
              onClick={openConfirm}
              className="inline-flex items-center rounded-full bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save schedule'}
            </button>
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-gray-200 p-4">
            <label className="block text-sm font-medium text-gray-700">Cycle length (months)</label>
            <input
              type="number"
              min={1}
              value={cycleMonths}
              onChange={handleCycleMonthsChange}
              className="mt-2 block w-full rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
          <div className="rounded-2xl border border-gray-200 p-4">
            <label className="block text-sm font-medium text-gray-700">Payouts per month</label>
            <input
              type="number"
              min={1}
              value={payoutsPerMonth}
              onChange={handlePayoutsPerMonthChange}
              className="mt-2 block w-full rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
          <div className="rounded-2xl border border-gray-200 p-4">
            <p className="text-sm font-medium text-gray-700">Summary</p>
            <p className="mt-2 text-sm text-gray-600">
              Total payout slots:{' '}
              <span className="font-semibold text-gray-900">
                {totalSlots} {totalSlots === 1 ? 'slot' : 'slots'}
              </span>
            </p>
            <p className="text-sm text-gray-600">
              Payout events scheduled:{' '}
              <span className="font-semibold text-gray-900">{totalPayoutEvents}</span>
            </p>
            <p className="text-sm text-gray-600">
              Slots with multiple members:{' '}
              <span className="font-semibold text-gray-900">{stackedSlots}</span>
            </p>
            <p className="text-sm text-gray-600">
              Members:{' '}
              <span className="font-semibold text-gray-900">
                {members.length} {members.length === 1 ? 'person' : 'people'}
              </span>
            </p>
            <p className="text-sm text-gray-600">
              Slots per month:{' '}
              <span className="font-semibold text-gray-900">{payoutsPerMonth}</span>
            </p>
          </div>
        </div>

        <div className="mt-4">
          {!allSlotsFilled ? (
            <div className="rounded-2xl border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-900">
              Assign at least one member to every slot. You can stack multiple members in a slot if totals exceed the
              number of available slots.
            </div>
          ) : !allMembersAssigned ? (
            <div className="rounded-2xl border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-900">
              Each member must appear at least once. Adjust assignments or auto-distribute to include everyone.
            </div>
          ) : (
            <div className="rounded-2xl border border-green-200 bg-green-50 p-4 text-sm text-green-900">
              This configuration covers all members. Payouts are split by each member&apos;s desired payout count, even
              when multiple members share the same slot. Saves are blocked if monthly totals exceed contributions.
            </div>
          )}
          {scheduleError && (
            <div className="mt-3 rounded-2xl border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-900">
              Latest saved schedule could not be loaded: {scheduleError}
            </div>
          )}
          {saveError && (
            <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              {saveError}
            </div>
          )}
          {saveSuccess && (
            <div className="mt-3 rounded-2xl border border-green-200 bg-green-50 p-4 text-sm text-green-900">
              Schedule saved. The payout table below will now reflect this configuration.
            </div>
          )}
        </div>

        <div className="mt-6">
          <p className="text-sm font-semibold text-gray-900 mb-3">Assignments overview</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {members.map((member) => {
              const count = assignmentCounts[member.id] || 0
              const baseContribution = getBaseContribution(member)
              const expectedTotal = baseContribution * cycleMonths
              const desiredPayouts = member.desired_payouts_per_cycle
              const balanced = count > 0
              const hasMismatch = desiredPayouts && count !== desiredPayouts
              return (
                <div
                  key={member.id}
                  className={`rounded-2xl border p-4 ${
                    balanced ? (hasMismatch ? 'border-yellow-300 bg-yellow-50/60' : 'border-gray-200 bg-gray-50') : 'border-yellow-300 bg-yellow-50/60'
                  }`}
                >
                  <p className="text-sm font-semibold text-gray-900">{member.name || member.email || 'Unnamed'}</p>
                  <p className="text-xs text-gray-500">{member.email || 'No email provided'}</p>
                  <p className="mt-2 text-sm text-gray-700">
                    Actual slots: {count > 0 ? count : '0'} {count === 1 ? 'payout' : 'payouts'}
                    {desiredPayouts && (
                      <span className="ml-2 text-xs text-gray-600">
                        (Desired: {desiredPayouts})
                        {hasMismatch && <span className="ml-1 text-yellow-600 font-semibold">⚠ Mismatch</span>}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500">
                    Contribution: ${baseContribution.toFixed(2)} · Expected total: ${expectedTotal.toFixed(2)}
                  </p>
                  {memberPayoutTotals[member.id] && (
                    <div className="mt-2 text-xs">
                      {memberPayoutTotals[member.id].custom > 0 && (
                        <p className="text-gray-600">
                          Custom: ${memberPayoutTotals[member.id].custom.toFixed(2)}
                        </p>
                      )}
                      {memberPayoutTotals[member.id].autoSlotsCount > 0 && (
                        <p className="text-gray-600">
                          Auto: ${memberPayoutTotals[member.id].auto.toFixed(2)} × {memberPayoutTotals[member.id].autoSlotsCount} slots
                        </p>
                      )}
                      <p className={`font-semibold ${
                        Math.abs(memberPayoutTotals[member.id].difference) < 0.01
                          ? 'text-green-600'
                          : 'text-red-600'
                      }`}>
                        Total: ${memberPayoutTotals[member.id].total.toFixed(2)} / ${memberPayoutTotals[member.id].expected.toFixed(2)}
                      </p>
                      {Math.abs(memberPayoutTotals[member.id].difference) >= 0.01 && (
                        <p className="text-red-600 text-[10px]">
                          Difference: ${memberPayoutTotals[member.id].difference.toFixed(2)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-gray-900">Monthly payout slots</p>
            <span className="text-xs text-gray-500">
              {dirty ? 'Custom changes applied' : 'Auto-distributed'}
            </span>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {scheduleMatrix.map((slots, monthIndex) => (
              <div key={`month-${monthIndex}`} className="rounded-2xl border border-gray-200 p-4 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-gray-900">Month {monthIndex + 1}</p>
                  <span className="text-xs text-gray-500">
                    {slots.filter((slot) => 
                      slot.some((memberObj) => {
                        const memberId = typeof memberObj === 'object' ? memberObj?.member_id : memberObj
                        return Boolean(memberId)
                      })
                    ).length}/{payoutsPerMonth} base slots filled
                  </span>
                </div>
                <div className="space-y-3">
                  {slots.map((slotMembers, slotIndex) => {
                    const globalSlotIndex = monthIndex * payoutsPerMonth + slotIndex
                    return (
                      <div key={`slot-${monthIndex}-${slotIndex}`} className="rounded-xl border border-gray-100 p-3 bg-gray-50/60">
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-xs font-semibold text-gray-500">
                            Payout #{slotIndex + 1}
                          </label>
                          <span className="text-[11px] text-gray-500">
                            {slotMembers.filter((memberObj) => {
                              const memberId = typeof memberObj === 'object' ? memberObj?.member_id : memberObj
                              return Boolean(memberId)
                            }).length} member(s)
                          </span>
                        </div>
                        <div className="space-y-2">
                          {slotMembers.map((memberObj, memberIndex) => {
                            const memberId = typeof memberObj === 'object' ? memberObj?.member_id : memberObj
                            const customAmount = typeof memberObj === 'object' ? memberObj?.custom_amount : null
                            const hasValue = Boolean(memberId)
                            
                            // Calculate expected amount for this member
                            const member = members.find(m => m.id === memberId)
                            const memberTotal = memberPayoutTotals[memberId]
                            let calculatedAmount = 0
                            if (memberTotal && memberTotal.auto > 0) {
                              calculatedAmount = memberTotal.auto
                            } else if (member) {
                              // Fallback calculation if totals not available yet
                              const contribution = getBaseContribution(member)
                              const expectedTotal = contribution * cycleMonths
                              const slotCount = assignmentCounts[memberId] || 1
                              calculatedAmount = expectedTotal / slotCount
                            }
                            
                            return (
                              <div key={`slot-${globalSlotIndex}-${memberIndex}`} className="space-y-2">
                                <div className="flex items-center space-x-2">
                                  <select
                                    value={memberId || ''}
                                    onChange={(event) =>
                                      handleAssignmentChange(globalSlotIndex, memberIndex, event.target.value)
                                    }
                                    className={`block flex-1 rounded-2xl border px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 ${
                                      hasValue ? 'border-gray-200 bg-white' : 'border-yellow-200 bg-yellow-50'
                                    }`}
                                  >
                                    <option value="">Select member</option>
                                    {members.map((m) => (
                                      <option key={m.id} value={m.id}>
                                        {m.name || m.email || 'Unnamed'}
                                      </option>
                                    ))}
                                  </select>
                                  {slotMembers.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveSlotMember(globalSlotIndex, memberIndex)}
                                      className="text-xs text-gray-500 hover:text-red-600 px-2 whitespace-nowrap"
                                      title="Remove from this slot"
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                                {hasValue && (
                                  <div className="bg-white rounded-lg border border-gray-200 p-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Payout Amount (optional)
                                    </label>
                                    <div className="flex items-center space-x-2">
                                      <span className="text-sm text-gray-500">$</span>
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={customAmount ?? ''}
                                        onChange={(e) => handleAmountChange(globalSlotIndex, memberIndex, e.target.value)}
                                        placeholder={calculatedAmount > 0 ? calculatedAmount.toFixed(2) : 'Auto-calculated'}
                                        className="block flex-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                                      />
                                      {customAmount != null && (
                                        <button
                                          type="button"
                                          onClick={() => handleResetAmount(globalSlotIndex, memberIndex)}
                                          className="text-xs text-primary-600 hover:text-primary-700 whitespace-nowrap"
                                          title="Reset to auto-calculated"
                                        >
                                          Reset
                                        </button>
                                      )}
                                    </div>
                                    {customAmount == null && calculatedAmount > 0 && (
                                      <p className="mt-1 text-[10px] text-gray-500">
                                        Auto: ${calculatedAmount.toFixed(2)}
                                      </p>
                                    )}
                                    {memberTotal && Math.abs(memberTotal.difference) >= 0.01 && (
                                      <p className="mt-1 text-[10px] text-red-600">
                                        Warning: Total doesn&apos;t match expected amount
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )
                          })}
                          <button
                            type="button"
                            onClick={() => handleAddSlotMember(globalSlotIndex)}
                            className="text-xs text-primary-700 hover:text-primary-800"
                          >
                            + Add another payout to this slot
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false)
          handleSave()
        }}
        summary={{
          cycleMonths,
          payoutsPerMonth,
          totalSlots,
          totalPayoutEvents,
          stackedSlots,
          membersCount: members.length,
        }}
      />
    </div>
  )
}

function ConfirmModal({ open, onClose, onConfirm, summary }) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-gray-100">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h4 className="text-lg font-semibold text-gray-900">Save payout schedule?</h4>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-gray-600">
            Confirm these settings. Payouts will be saved with stacked slots where configured, and monthly guardrails
            will block overpaying beyond contributions.
          </p>
          <ul className="text-sm text-gray-800 space-y-1">
            <li>Cycle length: <span className="font-semibold">{summary.cycleMonths} months</span></li>
            <li>Payouts per month: <span className="font-semibold">{summary.payoutsPerMonth}</span></li>
            <li>Total slots: <span className="font-semibold">{summary.totalSlots}</span></li>
            <li>Payout events: <span className="font-semibold">{summary.totalPayoutEvents}</span></li>
            <li>Stacked slots: <span className="font-semibold">{summary.stackedSlots}</span></li>
            <li>Members: <span className="font-semibold">{summary.membersCount}</span></li>
          </ul>
        </div>
        <div className="flex justify-end items-center gap-3 px-5 py-4 border-t border-gray-100 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded-full bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700"
          >
            Confirm & save
          </button>
        </div>
      </div>
    </div>
  )
}
